import type { AppRouter } from "@papier/api";
import { Alert, AlertDescription, AlertTitle } from "@papier/ui/components/ui/alert";
import { Button } from "@papier/ui/components/ui/button";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { CatchNotFound, useRouter } from "@tanstack/react-router";
import { TRPCClientError } from "@trpc/client";
import { AlertCircleIcon, ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { ProjectNotFound, TemplateNotFound } from "./not-found";

interface ErrorProps {
  error: TRPCClientError<AppRouter> | Error;
}

function useErrorReset() {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  return router;
}

export function ErrorAlert({ error, onRetry }: { error: string; onRetry?: () => void }) {
  console.error("Error Alert:", error);

  const router = useRouter();
  return (
    <Alert variant="destructive">
      <AlertCircleIcon />
      <AlertTitle>{error}</AlertTitle>
      <AlertDescription>Something went wrong. Please try again.</AlertDescription>
      <div className="flex gap-3 pt-5">
        <Button type="button" variant="destructive" onClick={onRetry}>
          Try Again
        </Button>
        <Button variant="ghost" className="text-foreground" onClick={() => router.history.back()}>
          <ArrowLeft />
          Back
        </Button>
      </div>
    </Alert>
  );
}

export function ProjectErrorComponent({ error }: ErrorProps) {
  const router = useErrorReset();
  if (error instanceof TRPCClientError) {
    if (error.data?.code === "NOT_FOUND") {
      return (
        <CatchNotFound>
          <ProjectNotFound message={error.message} />
        </CatchNotFound>
      );
    }
  }
  return (
    <ErrorAlert
      error={error.message}
      onRetry={() => {
        router.invalidate();
      }}
    />
  );
}

export function TemplateErrorComponent({ error }: ErrorProps) {
  const router = useErrorReset();
  if (error instanceof TRPCClientError) {
    if (error.data?.code === "NOT_FOUND") {
      return (
        <CatchNotFound>
          <TemplateNotFound message={error.message} />
        </CatchNotFound>
      );
    }
  }
  return (
    <ErrorAlert
      error={error.message}
      onRetry={() => {
        router.invalidate();
      }}
    />
  );
}
